import type { Query, QueryClient } from '@tanstack/react-query'

/**
 * THE refresh after a record's value is written, wherever it was written (a table cell, a record
 * page, a pane). The paged list was missing, so an edit in the side pane — a site's building type —
 * stayed stale in the table until a reload (owner, 2026-10-23).
 *
 * Narrow on purpose ("everything that adds queries should be questioned"): of the list's reads only
 * those that HOLD the record refetch — the page it is on, that page's values, the calculated batch
 * it is in — not every loaded page. Only reads on screen refetch at once; the rest are marked stale.
 */
export function refreshAfterRecordWrite(queryClient: QueryClient, { slug, typeId, recordId }: {
  slug: string; typeId: string; recordId: string
}): void {
  const holds = (q: Query) => {
    const [, , ...rest] = q.queryKey as unknown[]
    if (rest.some((part) => typeof part === 'string' && part.split(',').includes(recordId))) return true
    const rows = (q.state.data as { rows?: { record_id?: string }[] } | undefined)?.rows
    return Array.isArray(rows) && rows.some((r) => r.record_id === recordId)
  }
  for (const queryKey of [['record', recordId], ['column-values', typeId], ['mrl'], ['timeline', slug, recordId]]) {
    void queryClient.invalidateQueries({ queryKey })
  }
  // a hierarchy's branch pages carry their rows' values inline (collection/useHierarchy)
  for (const queryKey of [['page', typeId], ['page-values', typeId], ['calc-batch', typeId], ['hierarchy-rows', typeId], ['hierarchy-children', typeId]]) {
    void queryClient.invalidateQueries({ queryKey, predicate: holds })
  }
  void queryClient.invalidateQueries({ queryKey: ['chips', slug], predicate: holds })
}

/**
 * THE refresh after a collection's SET changed — a record created or archived. Every collection
 * reads its rows page by page (collection/loadingPolicy.ts), so its pages, its count and its
 * summary are re-read; the values of the records already read are not (they did not change).
 */
export function refreshAfterSetChange(queryClient: QueryClient): void {
  for (const queryKey of [['mrl'], ['page'], ['page-locate'], ['collection-summary'], ['hierarchy-rows'], ['hierarchy-levels'], ['hierarchy-count'], ['hierarchy-children']]) {
    void queryClient.invalidateQueries({ queryKey })
  }
}
